import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { getDemoProducts, getLogoutRedirectPath, isDemoUser } from '../../services/demoSession'

export default function Dashboard() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [products, setProducts] = useState([])
  const [catalogProducts, setCatalogProducts] = useState([])
  const [licenseRequests, setLicenseRequests] = useState([])
  const [search, setSearch] = useState('')
  const [requestingProductId, setRequestingProductId] = useState(null)
  const [requestMessage, setRequestMessage] = useState('')
  const [requestError, setRequestError] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadingCatalog, setLoadingCatalog] = useState(false)
  const roles = user?.Roles || user?.roles || []
  const normalizedRoles = roles.map((role) => String(role || '').trim().toLowerCase())
  const canAccessAdminModule = normalizedRoles.includes('admin') || normalizedRoles.includes('superadmin')
  const hideProductFromDashboard = (product) => String(product?.slug || '').trim().toLowerCase() === 'brain-emprendedor'
  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    if (isDemoUser(user)) {
      const demoProducts = getDemoProducts()
      setProducts(demoProducts)
      setCatalogProducts(demoProducts.map((product) => ({
        ...product,
        hasActiveLicense: true,
        hasPendingRequest: false
      })))
      setLicenseRequests([])
      setLoading(false)
      return
    }

    try {
      await Promise.all([fetchProducts(), fetchCatalogProducts(), fetchLicenseRequests()])
    } catch (error) {
      console.error('Error loading dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchProducts = async () => {
    const response = await fetch('/api/user/products', { credentials: 'include' })
    if (!response.ok) return
    const data = await response.json()
    setProducts(Array.isArray(data) ? data : [])
  }

  const fetchCatalogProducts = async (searchValue = '') => {
    setLoadingCatalog(true)
    try {
      const query = new URLSearchParams()
      if (searchValue.trim()) query.set('search', searchValue.trim())
      const response = await fetch(`/api/user/catalog/products${query.toString() ? `?${query.toString()}` : ''}`, {
        credentials: 'include'
      })
      if (!response.ok) return
      const data = await response.json()
      setCatalogProducts(Array.isArray(data) ? data : [])
    } finally {
      setLoadingCatalog(false)
    }
  }

  const fetchLicenseRequests = async () => {
    const response = await fetch('/api/user/license-requests', { credentials: 'include' })
    if (!response.ok) return
    const data = await response.json()
    setLicenseRequests(Array.isArray(data) ? data : [])
  }

  const handleLogout = async () => {
    const redirectPath = getLogoutRedirectPath(user, window.location.pathname)
    await logout()
    window.location.href = redirectPath
  }

  const getProductRoute = (product) => {
    const productRoutes = {
      'rentalbiz': '/inmobiliario/rentalbiz',
      'appointments': '/agendalab',
      'agendalab': '/agendalab',
      'material-calculator': '/material-calculator/projects',
      'rentaltech': '/rentaltech/dashboard',
      'entrepreneurcatalog': '/entrepreneurcatalog/products'
    }
    return productRoutes[product.slug] || `/products/${product.slug}`
  }

  const handleProductClick = (product) => {
    navigate(getProductRoute(product))
  }

  const handleSearchSubmit = async (e) => {
    e.preventDefault()
    setRequestError('')
    await fetchCatalogProducts(search)
  }

  const handleRequestLicense = async (product) => {
    if (isDemoUser(user)) {
      setRequestError('')
      setRequestMessage(`Modo demo: ${product.name} ya esta activo para exploracion.`)
      return
    }

    setRequestError('')
    setRequestMessage('')
    setRequestingProductId(product.id)

    try {
      const response = await fetch('/api/user/license-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ productId: product.id })
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.message || 'No se pudo crear la solicitud')
      }

      setRequestMessage(`Solicitud enviada para ${product.name}.`)
      await Promise.all([fetchCatalogProducts(search), fetchLicenseRequests()])
    } catch (error) {
      setRequestError(error.message || 'No se pudo crear la solicitud')
    } finally {
      setRequestingProductId(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-primary-600">AppEmprendedor</h1>
          <div className="flex items-center space-x-4">
            <span className="text-gray-700">Hola, {user?.firstName || user?.email}</span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Mis Productos</h2>

        {canAccessAdminModule && (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => navigate('/admin/licenses')}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Ir al módulo administrativo
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No tienes productos asignados.</p>
            <p className="text-gray-400 mt-2">Contacta al administrador para obtener acceso a los productos.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {canAccessAdminModule && (
              <div
                onClick={() => navigate('/admin/licenses')}
                className="block bg-primary-50 border border-primary-200 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden cursor-pointer"
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">A</span>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-primary-900">Módulo administrativo</h3>
                      <p className="text-sm text-primary-700">Gestión</p>
                    </div>
                  </div>
                  <p className="text-primary-800 text-sm">
                    Accede a licencias, usuarios, esquema y configuración administrativa.
                  </p>
                </div>
              </div>
            )}

            {products.filter((product) => !hideProductFromDashboard(product)).map((product) => (
              <div
                key={product.id}
                onClick={() => handleProductClick(product)}
                className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden cursor-pointer"
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    {product.iconUrl ? (
                      <img src={product.iconUrl} alt={product.name} className="w-12 h-12 rounded-lg" />
                    ) : (
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                        <span className="text-2xl font-bold text-primary-600">
                          {product.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
                      <p className="text-sm text-gray-500">{product.category?.name || 'Sin categoría'}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {product.description || 'Sin descripción disponible'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        <section className="mt-10">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Ecosistema AppEmprendedor</h3>
          <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, slug o descripción"
              className="w-full sm:max-w-xl px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
              Buscar
            </button>
          </form>

          {requestMessage && (
            <div className="mb-3 rounded-md border border-green-200 bg-green-50 px-4 py-2 text-green-700 text-sm">
              {requestMessage}
            </div>
          )}

          {requestError && (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-red-700 text-sm">
              {requestError}
            </div>
          )}

          {loadingCatalog ? (
            <div className="py-6 text-gray-500">Cargando catálogo...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {catalogProducts.filter((product) => !hideProductFromDashboard(product)).map((product) => {
                const hasPendingRequest = product.hasPendingRequest || licenseRequests.some((req) => req.productId === product.id && req.status === 'pending')
                return (
                  <div key={product.id} className="bg-white rounded-lg shadow-md border border-gray-100 p-5">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="text-lg font-semibold text-gray-900">{product.name}</h4>
                      {product.hasActiveLicense ? (
                        <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">Activa</span>
                      ) : hasPendingRequest ? (
                        <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800">Pendiente</span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">Disponible</span>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 mt-2">{product.description || 'Sin descripción disponible'}</p>
                    <p className="text-xs text-gray-500 mt-2">{product.category?.name || 'Sin categoría'}</p>

                    <div className="mt-4">
                      {product.hasActiveLicense ? (
                        <button
                          type="button"
                          onClick={() => handleProductClick(product)}
                          className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                        >
                          Abrir módulo
                        </button>
                      ) : hasPendingRequest ? (
                        <button
                          type="button"
                          className="w-full px-4 py-2 bg-amber-100 text-amber-800 rounded-md cursor-not-allowed"
                          disabled
                        >
                          Solicitud en revisión
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRequestLicense(product)}
                          disabled={requestingProductId === product.id}
                          className="w-full px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {requestingProductId === product.id ? 'Enviando...' : 'Solicitar licencia'}
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  )
}
